import {
    type CustomCell,
    type CustomRenderer,
    getMiddleCenterBias,
    GridCellKind,
    interpolateColors,
    type Rectangle,
    type Theme,
} from "@glideapps/glide-data-grid";
import { roundedRect } from "../draw-fns";

type PackedColor = string | readonly [normal: string, hover: string];

export interface MultiButton {
    readonly title: string;
    readonly url: string;
    readonly newTab?: boolean;
}

interface MultiButtonCellProps {
    readonly kind: "multi-button-cell";
    readonly buttons: readonly MultiButton[];
    readonly backgroundColor?: PackedColor;
    readonly color?: PackedColor;
    readonly borderColor?: PackedColor;
    readonly borderRadius?: number;
}

export type MultiButtonCell = CustomCell<MultiButtonCellProps> & { readonly: true };

function unpackColor(color: PackedColor, theme: Record<string, any>, hoverAmount: number): string {
    if (typeof color === "string") {
        if (theme[color] !== undefined) return theme[color];
        return color;
    }

    let [normal, hover] = color;
    if (theme[normal] !== undefined) normal = theme[normal];
    if (theme[hover] !== undefined) hover = theme[hover];
    return interpolateColors(normal, hover, hoverAmount);
}

function getButtonBounds(
    bounds: Rectangle,
    index: number,
    total: number,
    theme: Theme
): Rectangle {
    const spacing = 6;
    const totalSpacing = spacing * (total - 1);
    const availableWidth = bounds.width - theme.cellHorizontalPadding * 2 - totalSpacing;
    const buttonWidth = availableWidth / total;
    const x = Math.floor(
        bounds.x + theme.cellHorizontalPadding + index * (buttonWidth + spacing)
    );
    const y = Math.floor(bounds.y + theme.cellVerticalPadding + 1);
    const width = Math.ceil(buttonWidth);
    const height = Math.ceil(bounds.height - theme.cellVerticalPadding * 2 - 1);
    return { x, y, width, height };
}

function getIsHoveredButton(
    bounds: Rectangle,
    posX: number | undefined,
    posY: number | undefined,
    theme: Theme,
    total: number
): number | undefined {
    if (posX === undefined || posY === undefined) return undefined;
    for (let i = 0; i < total; i++) {
        const rect = getButtonBounds(bounds, i, total, theme);
        const inside =
            posX + bounds.x >= rect.x &&
            posX + bounds.x < rect.x + rect.width &&
            posY + bounds.y >= rect.y &&
            posY + bounds.y < rect.y + rect.height;
        if (inside) return i;
    }
    return undefined;
}

export const MultiButtonRenderer: CustomRenderer<MultiButtonCell> = {
    kind: GridCellKind.Custom,
    isMatch: (c): c is MultiButtonCell => (c.data as any).kind === "multi-button-cell",
    needsHoverPosition: true,
    needsHover: true,
    onSelect: a => a.preventDefault(),
    onClick: a => {
        const { cell, theme, bounds, posX, posY } = a;
        const { buttons } = cell.data;
        const hoveredIndex = getIsHoveredButton(bounds, posX, posY, theme, buttons.length);
        if (hoveredIndex !== undefined) {
            const button = buttons[hoveredIndex];
            if (button.newTab) {
                window.open(button.url, "_blank", "noopener,noreferrer");
            } else {
                window.location.href = button.url;
            }
        }
        return undefined;
    },
    drawPrep: args => {
        const { ctx } = args;
        ctx.textAlign = "center";
        return {
            deprep: a => {
                a.ctx.textAlign = "start";
            },
        };
    },
    draw: (args, cell) => {
        const { ctx, theme, rect, hoverX, hoverY, frameTime, drawState } = args;
        const { buttons, backgroundColor, color, borderColor, borderRadius } = cell.data;
        const total = buttons.length;

        interface DrawState {
            readonly hoveredIndex: number | undefined;
            readonly animationStartTime: number;
        }

        // eslint-disable-next-line prefer-const
        let [state, setState] = drawState as [DrawState | undefined, (s: DrawState) => void];

        const hoveredIndex = getIsHoveredButton(rect, hoverX, hoverY, theme, total);
        if (hoveredIndex !== undefined) args.overrideCursor?.("pointer");

        state ??= { hoveredIndex: undefined, animationStartTime: 0 };

        if (hoveredIndex !== state.hoveredIndex) {
            state = { hoveredIndex, animationStartTime: frameTime };
            setState(state);
        }

        const progress = Math.min(1, (frameTime - state.animationStartTime) / 200);
        const hoverAmount = hoveredIndex !== undefined ? progress : 1 - progress;

        if (progress < 1) args.requestAnimationFrame?.();

        for (let i = 0; i < total; i++) {
            const btn = buttons[i];
            const bRect = getButtonBounds(rect, i, total, theme);
            const isHovered = i === hoveredIndex;

            const localHoverAmount = isHovered ? hoverAmount : 0;

            if (backgroundColor !== undefined) {
                ctx.beginPath();
                roundedRect(ctx, bRect.x, bRect.y, bRect.width, bRect.height, borderRadius ?? theme.roundingRadius ?? 0);
                ctx.fillStyle = unpackColor(backgroundColor, theme, localHoverAmount);
                ctx.fill();
            }

            if (borderColor !== undefined) {
                ctx.beginPath();
                roundedRect(
                    ctx,
                    bRect.x + 0.5,
                    bRect.y + 0.5,
                    bRect.width - 1,
                    bRect.height - 1,
                    borderRadius ?? theme.roundingRadius ?? 0
                );
                ctx.strokeStyle = unpackColor(borderColor, theme, localHoverAmount);
                ctx.lineWidth = 1;
                ctx.stroke();
            }

            ctx.fillStyle = unpackColor(color ?? theme.accentColor, theme, localHoverAmount);
            ctx.fillText(
                btn.title,
                bRect.x + bRect.width / 2,
                bRect.y + bRect.height / 2 + getMiddleCenterBias(ctx, theme.baseFontFull)
            );
        }

        return true;
    },
    provideEditor: undefined,
};
